<template>
    <v-form @submit.prevent="saveUpload" ref="form">
        <v-card class="elavation-1" style="border-radius:4px;">
            <v-toolbar flat height="45px">
                <span class="font-weight-bold">{{ mode }} 201 file</span>
                <v-spacer></v-spacer>
                 <v-btn icon @click.native="closeDialog" :disabled="disabled">
                    <v-icon >close</v-icon>
                </v-btn>
            </v-toolbar>
            <v-progress-linear v-if="loading_uploading" indeterminate height="2" style="margin-top:-3px;"></v-progress-linear>
            <v-alert v-if="error" :value="true" color="error" icon="error" outline style="margin:10px 0px 10px 0px;">
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>  
            <v-card-text class="text-xs-center" style="margin-top:-15px">
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-content">
                        <v-autocomplete
                            v-model="documentId"
                            :disabled="disabled"
                            :items="documentTypes"
                            :loading="loading_documents"
                            :rules="[rules.required]"
                            chips
                            color="blue-grey lighten-2"
                            label="Document Type"
                            item-text="documentName"
                            item-value="documentId"
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi">
                                    {{ data.item.documentName }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template v-if="typeof data.item !== 'object'">
                                    <v-list-tile-content v-text="data.item"></v-list-tile-content>
                                </template>
                                <template v-else>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.documentName"></v-list-tile-title>
                                        <v-list-tile-sub-title v-html="data.item.group"></v-list-tile-sub-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-content">
                        <v-autocomplete
                            v-model="folderIds"
                            :disabled="disabled"
                            :items="folders"
                            :loading="loading_folders"
                            :rules="[rules.required_list]"
                            chips
                            color="blue-grey lighten-2"
                            label="Save to folders"
                            item-text="folderName"
                            item-value="folderId"
                            multiple
                            >
                            <template v-slot:selection="data">
                                <v-chip :selected="data.selected" class="chip--select-multi">
                                    <v-icon :color="data.item.color">folder</v-icon>&nbsp;&nbsp;
                                    {{ data.item.folderName }}
                                </v-chip>
                            </template>
                            <template v-slot:item="data">
                                <template>
                                    <v-list-tile-content>
                                        <v-list-tile-title v-html="data.item.folderName"></v-list-tile-title>
                                    </v-list-tile-content>
                                </template>
                            </template>
                        </v-autocomplete>
                    </v-flex>
                </v-layout>
                <v-layout row wrap>
                    <v-flex xs12 sm12 class="form-content">
                        <v-text-field label="Label"
                            v-model="label"
                            :rules="[rules.required]"
                            maxlength="50"
                            hint="Max 50 alphanumeric characters"
                            :disabled="disabled"></v-text-field>
                    </v-flex>
                </v-layout>
                <v-layout row wrap>
                    <v-flex v-if="!uploadFileName" xs12 sm12 class="form-content">
                        <div class="text-xs-center">
                            <vue-dropzone ref="myVueDropzone" 
                                class="form-upload"
                                id="dropzone" 
                                :options="dropzoneOptions"
                                v-on:vdropzone-success="uploadSuccess"
                                v-on:vdropzone-file-added="fileAdded"
                                ></vue-dropzone>
                        </div>
                        <div v-if="displayNoFileUpload" class="red--text text-xs-left">File upload is required.</div>
                    </v-flex> 
                    <v-flex v-else xs12 sm12 class="form-content uploaded text-xs-left">
                        <span>Uploaded file</span><br>
                        <span style="color:grey;">
                            {{ uploadFileName }}
                        </span>
                        <div class="delete-container">
                            <v-btn icon small @click="showDeleteFile" :disabled="disabled" title="Delete uploaded file">
                                <v-icon small>delete</v-icon>
                            </v-btn>
                        </div>
                    </v-flex>
                </v-layout>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions style="margin-right:25px;">
                <v-spacer></v-spacer>
                <v-btn v-if="!isAddNewMode" @click="showDeleteConfirmation" class="error" dark :loading="loading_delete">Delete</v-btn>
                <v-btn v-if="isDocumentCOE" class="primary" @click="showGenerateCOE" :disabled="disabled">Generate</v-btn>
                <v-btn type="submit" class="primary" :disabled="!completeFormData" :loading="loading">Save</v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="dialogOkCancel" persistent width="600">
            <ok-cancel :msg="dialogOkCancelMsg" :dialogOkCancel.sync="dialogOkCancel" @ok="onOkCancel"></ok-cancel>
        </v-dialog>
        <v-dialog v-model="dialogGenerateCOE" persistent width="500">
            <generate-coe ref="refGenerateCOE" :msg="dialogGenerateCOE" :dialogGenerateCOE.sync="dialogGenerateCOE" @send="generateCOEHandler"></generate-coe >
        </v-dialog>
    </v-form>
</template>
<script>
    
import axios from 'axios'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import hr201Api from '@/api/hr201'
import pageKeys from '@/common/pagekeys'

export default {
    name: 'upload-attachment',
    components: {
        vueDropzone: vue2Dropzone,
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
        'generate-coe': ()=> import("@/components/201Files/GenerateCOE"),
    },
    data () {
        return {
            error: false,
            errorMessage: '',
            loading: false,  
            loading_documents: false,
            loading_folders: false,
            loading_uploading: false,
            loading_delete: false,
            disabled: false,
            rules: {
                required: value => !!value || 'Required.',
                required_list: value => !!value.length || 'Required.',
            },    
            dropzoneOptions: {                    
                url: `${axios.defaults.baseURL}HR201/upload`,    
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "page_key": pageKeys.twozeroone_files,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                params: () => {
                    return {
                        userId: this.userId
                    }
                },
                addRemoveLinks: false,
                dictDefaultMessage: "<b class='subheading'><u>Drag and drop your file or click to browse</u><b><br><br><p class='caption'>Maximum 5MB per file.<br>Accepted files types: <br> Images (.jpg, .jpeg, .png), PDFs (.pdf), Word documents (.doc, .docx)</p>",
                maxFiles: 1,
                acceptedFiles: [
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    ].join(','),
                maxFilesize: 5,   // 5MB
            },
            dialogOkCancel: false, 
            dialogOkCancelMsg: '',
            okCancelFunction: null,
            uploading: false,
            dialogGenerateCOE: false,
            fileUploadId: null,
            label: null,
            folderIds: [],
            userId: null,
            uploadFileName: null,
            uploadFileType: null,
            displayNoFileUpload: false,
            documentId: null,
            documentTypes: [],
            folders: [],
            mode: null,
        }
    },
    methods: {
        generateCOEHandler(data) {
            this.uploadFileName = data.fileName
            this.uploadFileType = data.fileType
            this.dialogGenerateCOE = false
        },
        showGenerateCOE() {
            this.$refs.refGenerateCOE.setParam(this.userId)
            this.dialogGenerateCOE = true
        },
        fileAdded(file) {
            this.loading_uploading = true
        },
        async uploadSuccess(file, response) {
            this.$refs.myVueDropzone.removeAllFiles(true)
            this.uploadFileName = response.fileName
            this.uploadFileType = response.fileType
            this.loading_uploading = false
        },
        async saveUpload() {
            try {
                if (this.$refs.form.validate() && this.completeFormData) {
                    this.disabled = true
                    this.loading = true
                    const data = {
                        fileUploadId: this.fileUploadId,
                        userId: this.userId,
                        documentId: this.documentId,
                        fileName: this.uploadFileName,
                        fileType: this.uploadFileType,
                        label: this.label,
                        folders: this.folderIds
                    }
                    const response = await hr201Api.saveUpload(data)
                    if (response && response.status == 200) {
                        this.clearForm()
                        this.$emit('send', null)
                        this.$emit('update:dialogUpload', false)
                    }
                    this.disabled = false
                    this.loading = false
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async deleteFile() {
            try {
                //this deletes only the uploaded file
                const data = {
                    userId: this.userId,
                    fileName: this.uploadFileName
                }
                const response = await hr201Api.deleteFile(data)
                if (response && response.status == 200) {
                    this.uploadFileName = null
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showDeleteFile() {
            this.dialogOkCancelMsg = 'This action will delete the uploaded file.<br> Do you want to proceed?'
            this.dialogOkCancel = true
            this.okCancelFunction = () => {
                if (this.mode == 'Upload') {
                    this.deleteFile()
                }else{
                    //do not delete the file in AWS directly so that when user cancels edit
                    //the file still exist
                    this.uploadFileName = null
                }
            }
        },
        onOkCancel() {
            if (typeof this.okCancelFunction === 'function') {
                this.okCancelFunction()
            }
            this.okCancelFunction = null
            this.dialogOkCancel = false
        },
        hasUploadedFile() {
            if (this.uploadFileName) {
                this.displayNoFileUpload = false
                return true
            }else{
                this.displayNoFileUpload = true
                return false
            }
        },
        async setParams(folderId, userId) {
            this.clearForm()
            this.mode = "Upload"
            this.userId = userId
            
            //always update folder list because new folders may exist or existing folders may have been updated
            await this.getFolders()
            this.folderIds = [folderId]

            if (!this.documentTypes.length) {
                await this.getDocumentTypes()
            }
        },
        async setEdit(data) {
            this.mode = "Edit"
            await this.getFolders()

            if (!this.documentTypes.length) {
                await this.getDocumentTypes()
            }

            this.fileUploadId = data.fileUploadId
            this.userId = data.userId
            this.documentId = data.documentId
            this.folderIds = data.folders
            this.label = data.label
            this.uploadFileType = data.fileType
            this.uploadFileName = data.fileName
        },
        async getDocumentTypes() {
            try {
                this.loading_documents = true
                const response = await hr201Api.getDocumentTypes()
                if (response && response.status == 200) {
                    this.documentTypes = response.data.documentTypes
                }
                this.loading_documents = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getFolders(show_loading) {
            try{
                this.loading_folders = show_loading
                const response = await hr201Api.getFolders()
                if (response && response.status == 200) {
                    this.folders = response.data.folders
                }
                this.loading_folders = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async delete201Record() {
            try {
                //this deletes the 201 file record and its associated upload file
                this.loading_delete = true
                const data = {
                    fileUploadId: this.fileUploadId,
                    userId: this.userId,
                    fileName: this.uploadFileName,
                    label: this.label
                }
                const response = await hr201Api.delete201Record(data)
                if (response && response.status == 200) {
                    this.loading_delete = false
                    this.clearForm()
                    this.$emit('send', null)
                    this.$emit('update:dialogUpload', false)
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        showDeleteConfirmation() {
            this.dialogOkCancelMsg = 'You are about to delete this 201 record and its file.<br>Do you want to proceed?'
            this.dialogOkCancel = true
            this.okCancelFunction = () => this.delete201Record() //can pass any method here
        },
        closeDialog() {
            if (this.uploadFileName && this.isAddNewMode) {
                this.dialogOkCancelMsg = 'You have uploaded a file, but have not saved its details.<br>Closing this window will delete the uploaded file.<br>Do you want to proceed?'
                this.dialogOkCancel = true
                this.okCancelFunction = () => this.deleteUploadAndClosePopup() //can pass any method here
            }else{
                this.$emit('update:dialogUpload', false)
            }
        },
        clearForm() {
            this.$refs.form.reset()
            this.fileUploadId = null
            this.uploadFileName = null
            this.uploadFileType = null
            this.error = false
            this.errorMessage = ''
        },
        async deleteUploadAndClosePopup() {
            await this.deleteUpload()
            this.$emit('update:dialogUpload', false)
        },
    },        
    created() {},
    computed: {
        completeFormData() {
            return this.userId && 
                this.documentId && 
                this.label &&
                this.uploadFileName &&
                this.folderIds.length
        },
        isDocumentCOE() {
            const selectedDocument = this.documentTypes.filter(x => x.documentId == this.documentId)
            if (selectedDocument && selectedDocument.length) {
                return selectedDocument[0].documentName == 'Certificate of Employment (COE)'
            }
        },
        isAddNewMode() {
            return this.mode == 'Upload'
        }
    }
}
</script>
<style scoped>
.color-box {
  width: 100%;
  cursor: pointer;
  border: 1px solid grey;
  padding: 2px;
}
.form-content {
    padding:0px 20px 0px 20px;
    margin-top:-5x;
}
.form-folder {
    margin:14px 0px 0px 10px;
}
.form-upload {
    height:160px;
    margin:10px 0px 10px 0px;
    padding:0px 50px 0px 50px;
    border: 1px dashed darkgray
}
.delete-container {
    float:right;
    margin-top:-10px;
}
.uploaded {
    margin-top: 10px;
}
</style>